import { useState } from "react";
import { motion } from "framer-motion";
import axios from "axios";
import { CalendarCheck, CheckCircle2, MessageCircle, Loader2 } from "lucide-react";
import { CLINIC } from "@/lib/site";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const SLOTS = ["Morning (9–12)", "Afternoon (12–4)", "Evening (4–9)"];
const CONDITIONS = [
  "Back / Neck Pain",
  "Knee / Joint Pain",
  "Frozen Shoulder",
  "Sports Injury",
  "Stroke / Neuro Rehab",
  "Post-Surgery Recovery",
  "Sciatica / Nerve Pain",
  "Other (describe in notes)",
];

export default function BookingForm() {
  const [form, setForm] = useState({
    name: "",
    phone: "",
    email: "",
    condition: "",
    preferred_date: "",
    time_slot: "",
    notes: "",
  });
  const [sending, setSending] = useState(false);
  const [error, setError] = useState("");
  const [booking, setBooking] = useState(null);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const today = new Date().toISOString().split("T")[0];

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (!form.name || !form.phone || !form.condition || !form.preferred_date || !form.time_slot) {
      setError("Please fill your name, phone, condition, date and time slot.");
      return;
    }
    setSending(true);
    try {
      const res = await axios.post(`${API}/appointments`, form);
      setBooking(res.data);
    } catch (err) {
      setError(err?.response?.data?.detail || "Something went wrong. Please call us instead.");
    } finally {
      setSending(false);
    }
  };

  if (booking) {
    const waText = encodeURIComponent(
      `Hello Dr. Vipul Singh, I'm ${booking.name}. I've booked a physiotherapy appointment for ${booking.preferred_date} (${booking.time_slot}) at Kamana Physiotherapy. My concern: ${booking.condition}. Booking ID: ${booking.id.slice(0, 8).toUpperCase()}`
    );
    return (
      <motion.div
        initial={{ opacity: 0, scale: 0.96 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
        data-testid="booking-success-panel"
        className="bg-teal-soft border border-teal-brand/15 rounded-3xl p-8 sm:p-10 text-center"
      >
        <CheckCircle2 size={48} className="mx-auto text-teal-mint" />
        <h3 className="font-display text-3xl font-semibold text-teal-brand mt-4">
          Appointment Confirmed
        </h3>
        <p className="mt-2 text-inkmuted text-sm leading-relaxed">
          Thank you, {booking.name.split(" ")[0]}. Your request for{" "}
          <strong className="text-teal-brand">{booking.preferred_date}</strong> —{" "}
          <strong className="text-teal-brand">{booking.time_slot}</strong> is registered. Our team
          will call you shortly to finalise the time.
        </p>
        <p className="mono-label text-gold-deep mt-5" data-testid="booking-id">
          Booking ID · {booking.id.slice(0, 8).toUpperCase()}
        </p>
        <a
          href={`https://wa.me/919250823508?text=${waText}`}
          target="_blank"
          rel="noreferrer"
          data-testid="booking-whatsapp-confirm"
          className="mt-6 inline-flex items-center gap-2 bg-teal-brand text-alabaster font-semibold text-sm rounded-full px-6 py-3 hover:bg-teal-deep transition-colors"
        >
          <MessageCircle size={16} /> Confirm on WhatsApp
        </a>
      </motion.div>
    );
  }

  return (
    <form
      onSubmit={submit}
      data-testid="booking-form"
      className="bg-white border border-teal-brand/10 rounded-3xl p-6 sm:p-8 shadow-[0_20px_50px_-24px_rgba(6,68,64,0.25)]"
    >
      <div className="flex items-center gap-3">
        <span className="h-10 w-10 rounded-full bg-gold-soft text-gold-deep flex items-center justify-center">
          <CalendarCheck size={18} />
        </span>
        <div>
          <p className="mono-label text-gold-deep">Book an Appointment</p>
          <p className="text-xs text-inkmuted">We confirm every booking by phone within hours.</p>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-6">
        <label className="block">
          <span className="text-xs font-semibold text-teal-brand">Full Name *</span>
          <input
            data-testid="booking-input-name"
            value={form.name}
            onChange={set("name")}
            placeholder="Your name"
            className="mt-1.5 w-full rounded-xl border border-teal-brand/15 bg-alabaster/60 px-4 py-3 text-sm outline-none focus:border-teal-brand focus:bg-white transition-colors"
          />
        </label>
        <label className="block">
          <span className="text-xs font-semibold text-teal-brand">Phone Number *</span>
          <input
            data-testid="booking-input-phone"
            value={form.phone}
            onChange={set("phone")}
            placeholder="+91 ..."
            inputMode="tel"
            className="mt-1.5 w-full rounded-xl border border-teal-brand/15 bg-alabaster/60 px-4 py-3 text-sm outline-none focus:border-teal-brand focus:bg-white transition-colors"
          />
        </label>
        <label className="block">
          <span className="text-xs font-semibold text-teal-brand">Email (optional)</span>
          <input
            data-testid="booking-input-email"
            value={form.email}
            onChange={set("email")}
            placeholder="you@example.com"
            className="mt-1.5 w-full rounded-xl border border-teal-brand/15 bg-alabaster/60 px-4 py-3 text-sm outline-none focus:border-teal-brand focus:bg-white transition-colors"
          />
        </label>
        <label className="block">
          <span className="text-xs font-semibold text-teal-brand">Primary Concern *</span>
          <select
            data-testid="booking-select-condition"
            value={form.condition}
            onChange={set("condition")}
            className="mt-1.5 w-full rounded-xl border border-teal-brand/15 bg-alabaster/60 px-4 py-3 text-sm outline-none focus:border-teal-brand focus:bg-white transition-colors"
          >
            <option value="">Select your concern</option>
            {CONDITIONS.map((c) => (
              <option key={c} value={c}>
                {c}
              </option>
            ))}
          </select>
        </label>
        <label className="block">
          <span className="text-xs font-semibold text-teal-brand">Preferred Date *</span>
          <input
            type="date"
            min={today}
            data-testid="booking-input-date"
            value={form.preferred_date}
            onChange={set("preferred_date")}
            className="mt-1.5 w-full rounded-xl border border-teal-brand/15 bg-alabaster/60 px-4 py-3 text-sm outline-none focus:border-teal-brand focus:bg-white transition-colors"
          />
        </label>
        <div>
          <span className="text-xs font-semibold text-teal-brand">Preferred Slot *</span>
          <div className="mt-1.5 flex flex-wrap gap-2">
            {SLOTS.map((s) => (
              <button
                key={s}
                type="button"
                data-testid={`booking-slot-${s.split(" ")[0].toLowerCase()}`}
                onClick={() => setForm({ ...form, time_slot: s })}
                className={`text-xs font-semibold rounded-full px-4 py-2.5 border transition-all duration-200 ${
                  form.time_slot === s
                    ? "bg-teal-brand text-alabaster border-teal-brand"
                    : "bg-alabaster/60 text-inkmuted border-teal-brand/15 hover:border-teal-brand/40"
                }`}
              >
                {s}
              </button>
            ))}
          </div>
        </div>
      </div>

      <label className="block mt-4">
        <span className="text-xs font-semibold text-teal-brand">Notes / Medical History (optional)</span>
        <textarea
          data-testid="booking-input-notes"
          value={form.notes}
          onChange={set("notes")}
          rows={3}
          placeholder="Tell us briefly about your pain, injury or surgery..."
          className="mt-1.5 w-full rounded-xl border border-teal-brand/15 bg-alabaster/60 px-4 py-3 text-sm outline-none focus:border-teal-brand focus:bg-white transition-colors resize-none"
        />
      </label>

      {error && (
        <p data-testid="booking-error" className="mt-4 text-sm font-medium text-red-700 bg-red-50 border border-red-200 rounded-xl px-4 py-3">
          {error}
        </p>
      )}

      <button
        type="submit"
        disabled={sending}
        data-testid="booking-submit-button"
        className="mt-6 w-full flex items-center justify-center gap-2 bg-teal-brand hover:bg-teal-deep text-alabaster font-semibold rounded-full py-4 transition-all duration-200 active:scale-[0.98] disabled:opacity-60"
      >
        {sending ? (
          <>
            <Loader2 size={18} className="animate-spin" /> Booking...
          </>
        ) : (
          <>
            <CalendarCheck size={18} /> Request Appointment
          </>
        )}
      </button>
      <p className="mt-3 text-[11px] text-inkmuted text-center">
        Prefer to talk now? Call us on{" "}
        <a href={CLINIC.phoneHref} className="font-semibold text-teal-brand">
          {CLINIC.phone}
        </a>
      </p>
    </form>
  );
}
